// GLSL for the VFX pools (docs/ART-DIRECTION.md §1 VFX, §4 photosensitivity, §7 style targets). Every output is
// additive: rgb × alpha is added to the emissive target. Instances with alpha 0 are moved outside the clip volume,
// so free pool slots cost vertex work only.

/** Ring styles; the codes are per-instance floats (aStyle.x). */
export const RING_STYLE_CODES = { wire: 0, core: 1, static: 2, sweep: 3 } as const
export type RingStyle = keyof typeof RING_STYLE_CODES

const HASH = /* glsl */ `
float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}
`

/**
 * Surface-conforming ring: base vertices are (u = turn fraction, v = inner→outer). The ring is built on the sphere
 * of radius aShape.z around the unit normal aCenter, so a wide shockwave hugs the globe instead of cutting it.
 * Glitch jitter scales the angular radius per band, re-rolled uGlitchSteps times a second (0 when reduced).
 */
export const RING_VERTEX = /* glsl */ `
attribute vec3 aCenter;
attribute vec3 aTangent;
attribute vec4 aShape;
attribute vec4 aColor;
attribute vec4 aStyle;

uniform float uTime;
uniform float uGlitch;
uniform float uGlitchSteps;

varying vec2 vRing;
varying vec4 vColor;
varying vec4 vStyle;
varying float vDashes;

const float TAU = 6.28318530718;
const float GLITCH_BANDS = 12.0;
${HASH}
void main() {
  if (aColor.a <= 0.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  vRing = position.xy;
  vColor = aColor;
  vStyle = aStyle;
  vDashes = aShape.w;
  float band = mod(floor(position.x * GLITCH_BANDS), GLITCH_BANDS);
  float jitter = uGlitch * aStyle.y * (hash(vec2(band + aStyle.z, floor(uTime * uGlitchSteps))) - 0.5);
  float angle = mix(aShape.x, aShape.y, position.y) * (1.0 + jitter);
  float theta = position.x * TAU;
  vec3 bitangent = cross(aCenter, aTangent);
  vec3 radial = cos(theta) * aTangent + sin(theta) * bitangent;
  vec3 p = (aCenter * cos(angle) + radial * sin(angle)) * aShape.z;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
}
`

/**
 * wire   — bright inner and outer edges plus radial ticks (vDashes per turn): the wireframe shockwave.
 * core   — filled disc, hot centre falling off to the rim (v = 0 is the centre).
 * static — "damage static": sparse speckles re-rolled uStaticSteps times a second (frozen when reduced).
 * sweep  — band revealed around the turn up to aStyle.w with a brighter leading edge (ownership sweep).
 */
export const RING_FRAGMENT = /* glsl */ `
uniform float uTime;
uniform float uMotion;
uniform float uStaticSteps;

varying vec2 vRing;
varying vec4 vColor;
varying vec4 vStyle;
varying float vDashes;

const float EDGE_WIDTH = 0.25;
const float TICK_DUTY = 0.12;
const float TICK_GLOW = 0.6;
const vec2 STATIC_GRID = vec2(48.0, 10.0);
const float STATIC_DENSITY = 0.72;
const float SWEEP_BASE = 0.4;
const float SWEEP_EDGE = 0.1;
${HASH}
void main() {
  float style = vStyle.x;
  float u = vRing.x;
  float v = vRing.y;
  float glow;
  if (style < ${RING_STYLE_CODES.wire}.5) {
    float edges = max(1.0 - smoothstep(0.0, EDGE_WIDTH, v), 1.0 - smoothstep(0.0, EDGE_WIDTH, 1.0 - v));
    float ticks = vDashes > 0.0 ? step(fract(u * vDashes), TICK_DUTY) * TICK_GLOW : 0.0;
    glow = max(edges, ticks);
  } else if (style < ${RING_STYLE_CODES.core}.5) {
    float falloff = 1.0 - v;
    glow = falloff * falloff;
  } else if (style < ${RING_STYLE_CODES.static}.5) {
    vec2 cell = floor(vec2(u, v) * STATIC_GRID);
    float n = hash(cell + vec2(floor(uTime * uStaticSteps * uMotion), vStyle.z));
    glow = step(STATIC_DENSITY, n) * (1.0 - v);
  } else {
    float band = 1.0 - abs(v * 2.0 - 1.0);
    float lead = smoothstep(vStyle.w - SWEEP_EDGE, vStyle.w, u);
    glow = step(u, vStyle.w) * (SWEEP_BASE + (1.0 - SWEEP_BASE) * lead) * band;
  }
  gl_FragColor = vec4(vColor.rgb, vColor.a * glow);
}
`

/**
 * Ribbons (flight trails, dotted altitude arcs, launch streaks, intercept beams): world-space strips written by the
 * CPU. aTrail = (distance along in world units, −1..1 across, dashes per world unit, dash duty); aLook = (dash scroll
 * in cycles per second, white-hot core strength).
 */
export const RIBBON_VERTEX = /* glsl */ `
attribute vec4 aColor;
attribute vec4 aTrail;
attribute vec2 aLook;

varying vec4 vColor;
varying vec4 vTrail;
varying vec2 vLook;

void main() {
  vColor = aColor;
  vTrail = aTrail;
  vLook = aLook;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`

/**
 * Core/dash/alpha paint shared by every ribbon fragment (RibbonPool's CPU-written strips and the route sweeps' static
 * geometry): across = −1..1 across the strip, along = world distance from the path start, alpha the caller's own
 * envelope (the route shader ramps and clips it before calling). Additive, so alpha 0 contributes nothing.
 */
const RIBBON_PAINT = /* glsl */ `
uniform float uTime;
uniform vec3 uHotCore;

const float CORE_SHARPNESS = 6.0;

vec4 ribbonPaint(vec3 colour, float alpha, float along, float across, float dashesPerUnit, float dashDuty, float scroll, float core) {
  float acrossFrac = 1.0 - abs(across);
  float body = acrossFrac * acrossFrac;
  float coreFactor = pow(acrossFrac, CORE_SHARPNESS) * core;
  float dash = dashesPerUnit > 0.0 ? step(fract(along * dashesPerUnit - uTime * scroll), dashDuty) : 1.0;
  return vec4(mix(colour, uHotCore, coreFactor), alpha * (body + coreFactor) * dash);
}
`

export const RIBBON_FRAGMENT = /* glsl */ `
${RIBBON_PAINT}

varying vec4 vColor;
varying vec4 vTrail;
varying vec2 vLook;

void main() {
  gl_FragColor = ribbonPaint(vColor.rgb, vColor.a, vTrail.x, vTrail.y, vTrail.z, vTrail.w, vLook.x, vLook.y);
}
`

/**
 * Route sweeps (Render owner: VFX RouteTrailSystem): one static strip per physical setup edge, sampled once by the
 * route-ribbon cache. The vertex pair shares a base centre and offsets by ±aSide; uReverse reads the shared geometry
 * against the path (progress mirrored, dash distance measured from the far end). aProgress is the cumulative fraction
 * of the route at each centre point.
 */
export const ROUTE_TRAIL_VERTEX = /* glsl */ `
attribute vec3 aSide;
attribute vec2 aTrail;
attribute float aProgress;

uniform float uHalfWidth;
uniform float uReverse;
uniform float uRouteLength;

varying float vProgress;
varying vec2 vTrail;

void main() {
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position + aSide * uHalfWidth, 1.0);
  float reverse = uReverse;
  vProgress = mix(aProgress, 1.0 - aProgress, reverse);
  vTrail = vec2(mix(aTrail.x, uRouteLength - aTrail.x, reverse), aTrail.y);
}
`

/**
 * The moving reveal: bright only inside [uTail, uHead] on the full fixed strip, with the shared tail→head alpha ramp.
 * Because the whole strip is drawn and the fragment clips, the reveal can never shortcut a route's corners.
 */
export const ROUTE_TRAIL_FRAGMENT = /* glsl */ `
${RIBBON_PAINT}

uniform vec3 uTint;
uniform float uAlpha;
uniform float uTailAlpha;
uniform float uHead;
uniform float uTail;
uniform float uDashesPerUnit;
uniform float uDashDuty;
uniform float uScroll;
uniform float uCore;

varying float vProgress;
varying vec2 vTrail;

void main() {
  float span = max(uHead - uTail, 1e-5);
  float ramp = clamp((vProgress - uTail) / span, 0.0, 1.0);
  float reveal = step(uTail, vProgress) * step(vProgress, uHead);
  float alpha = uAlpha * mix(uTailAlpha, 1.0, ramp) * reveal;
  gl_FragColor = ribbonPaint(uTint, alpha, vTrail.x, vTrail.y, uDashesPerUnit, uDashDuty, uScroll, uCore);
}
`

/**
 * Camera-facing sprites (particles and label characters) sampling the glyph atlas. Sizes and offsets are angular
 * (world size per unit view depth), so a sprite keeps its screen size at every zoom. aMotion = (alpha, scale,
 * rotation); aLook = (rgb, atlas cell); aFrame = (width, height, offset x, offset y); aJitter = (glitch amplitude,
 * seed). The glitch offset is re-rolled uGlitchSteps times a second (0 when reduced).
 */
export const SPRITE_VERTEX = /* glsl */ `
attribute vec3 aCenter;
attribute vec3 aMotion;
attribute vec4 aLook;
attribute vec4 aFrame;
attribute vec2 aJitter;

uniform float uTime;
uniform float uGlitch;
uniform float uGlitchSteps;
uniform vec2 uAtlasGrid;

varying vec2 vUv;
varying vec4 vColor;
${HASH}
void main() {
  if (aMotion.x <= 0.0) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    return;
  }
  vec4 mvPosition = modelViewMatrix * vec4(aCenter, 1.0);
  float depth = -mvPosition.z;
  vec2 local = position.xy * aFrame.xy * aMotion.y;
  float c = cos(aMotion.z);
  float s = sin(aMotion.z);
  vec2 offset = aFrame.zw;
  offset.x += uGlitch * aJitter.x * (hash(vec2(aJitter.y, floor(uTime * uGlitchSteps))) - 0.5);
  mvPosition.xy += (vec2(c * local.x - s * local.y, s * local.x + c * local.y) + offset) * depth;
  float column = mod(aLook.w, uAtlasGrid.x);
  float row = floor(aLook.w / uAtlasGrid.x);
  vec2 corner = position.xy + 0.5;
  vUv = vec2((column + corner.x) / uAtlasGrid.x, 1.0 - (row + 1.0 - corner.y) / uAtlasGrid.y);
  vColor = vec4(aLook.rgb, aMotion.x);
  gl_Position = projectionMatrix * mvPosition;
}
`

export const SPRITE_FRAGMENT = /* glsl */ `
uniform sampler2D uAtlas;

varying vec2 vUv;
varying vec4 vColor;

void main() {
  gl_FragColor = vec4(vColor.rgb, vColor.a * texture2D(uAtlas, vUv).a);
}
`
